var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import { _PdfAbstractSyntaxElement } from './abstract-syntax';
import { _ConstructionType, _TagClassType, _UniversalType } from './enumerator';
import { _convertBytesToText } from './utils';
import { _handleExplicitConversion, _stringToBytes } from '../../../utils';
import { _PdfObjectIdentifier } from './identifier-mapping';
var _PdfUniqueEncodingElement = /** @class */ (function (_super) {
    __extends(_PdfUniqueEncodingElement, _super);
    function _PdfUniqueEncodingElement(tagClass, construction, tagNumber, value) {
        if (tagClass === void 0) { tagClass = _TagClassType.universal; }
        if (construction === void 0) { construction = _ConstructionType.primitive; }
        if (tagNumber === void 0) { tagNumber = _UniversalType.endOfContent; }
        if (value === void 0) { value = undefined; }
        var _this = _super.call(this) || this;
        _this._value = new Uint8Array(0);
        _this._encode(value);
        _this._tagClass = tagClass;
        _this._construction = construction;
        _this._setTagNumber(tagNumber);
        return _this;
    }
    _PdfUniqueEncodingElement.prototype._getValue = function () {
        if (Array.isArray(this._value)) {
            var encoded = this._encodeSequence(this._value);
            this._currentValueLength = encoded.length;
            return encoded;
        }
        var prim = _handleExplicitConversion(this._value);
        if (prim !== this._value) {
            this._value = prim;
        }
        this._currentValueLength = prim.length;
        return prim;
    };
    _PdfUniqueEncodingElement.prototype._setValue = function (v) {
        this._currentValueLength = v.length;
        this._value = v;
    };
    _PdfUniqueEncodingElement.prototype._getBooleanValue = function () {
        if (this._construction !== _ConstructionType.primitive) {
            throw new Error('boolean cannot be constructed.');
        }
        return this._decodeBoolean(this._getValue());
    };
    _PdfUniqueEncodingElement.prototype._setBooleanValue = function (value) {
        this._setValue(this._encodeBoolean(value));
    };
    _PdfUniqueEncodingElement.prototype._getBitString = function () {
        if (this._construction !== _ConstructionType.primitive) {
            throw new Error('Bit string cannot be constructed.');
        }
        return this._decodeBitString(this._getValue());
    };
    _PdfUniqueEncodingElement.prototype._setBitString = function (value) {
        this._setValue(this._encodeBitString(value));
    };
    _PdfUniqueEncodingElement.prototype._getOctetString = function () {
        if (this._construction !== _ConstructionType.primitive) {
            throw new Error('Octet string cannot be constructed.');
        }
        return new Uint8Array(this._getValue());
    };
    _PdfUniqueEncodingElement.prototype._setOctetString = function (value) {
        this._setValue(new Uint8Array(value));
    };
    _PdfUniqueEncodingElement.prototype._getObjectDescriptor = function () {
        return this._decodeObjectDescriptor(this._getValue());
    };
    _PdfUniqueEncodingElement.prototype._setObjectDescriptor = function (value) {
        this._setValue(this._encodeObjectDescriptor(value));
    };
    _PdfUniqueEncodingElement.prototype._getUtf8String = function () {
        if (this._construction !== _ConstructionType.primitive) {
            throw new Error('Unicode text cannot be constructed.');
        }
        return _convertBytesToText(this._getValue());
    };
    _PdfUniqueEncodingElement.prototype._setUtf8String = function (value) {
        this._setValue(_stringToBytes(value));
    };
    _PdfUniqueEncodingElement.prototype._getSequence = function () {
        if (this._construction !== _ConstructionType.constructed) {
            throw new Error('Set or sequence cannot be primitively constructed.');
        }
        if (Array.isArray(this._value)) {
            return this._value;
        }
        return this._decodeSequence(this._getValue());
    };
    _PdfUniqueEncodingElement.prototype._setSequence = function (value) {
        this._construct(value);
        this._construction = _ConstructionType.constructed;
    };
    _PdfUniqueEncodingElement.prototype._getAbstractSetValue = function () {
        var ret = this._getSequence();
        if (!this._isUniquelyTagged(ret)) {
            throw new Error('Duplicate tag in Set.');
        }
        return ret;
    };
    _PdfUniqueEncodingElement.prototype._setAbstractSetValue = function (value) {
        this._sortCanonically(value);
        this._setSequence(value);
    };
    _PdfUniqueEncodingElement.prototype._getSequenceOf = function () {
        if (this._construction !== _ConstructionType.constructed) {
            throw new Error('Set or sequence cannot be primitively constructed.');
        }
        if (Array.isArray(this._value)) {
            return this._value;
        }
        return this._decodeSequence(this._getValue());
    };
    _PdfUniqueEncodingElement.prototype._setSequenceOf = function (value) {
        this._construct(value);
        this._construction = _ConstructionType.constructed;
    };
    _PdfUniqueEncodingElement.prototype._getAbstractSetOf = function () {
        return this._getSequence();
    };
    _PdfUniqueEncodingElement.prototype._setAbstractSetOf = function (value) {
        this._setSequence(value);
    };
    _PdfUniqueEncodingElement.prototype._getNumericString = function () {
        if (this._construction !== _ConstructionType.primitive) {
            throw new Error('Numeric string cannot be constructed.');
        }
        return this._decodeNumericString(this._getValue());
    };
    _PdfUniqueEncodingElement.prototype._setNumericString = function (value) {
        this._setValue(this._encodeNumericString(value));
    };
    _PdfUniqueEncodingElement.prototype._getPrintableString = function () {
        if (this._construction !== _ConstructionType.primitive) {
            throw new Error('Printable ASCII string cannot be constructed.');
        }
        return this._decodePrintableString(this._getValue());
    };
    _PdfUniqueEncodingElement.prototype._setPrintableString = function (value) {
        this._setValue(this._encodePrintableString(value));
    };
    _PdfUniqueEncodingElement.prototype._getTeleprinterText = function () {
        return this._serialize();
    };
    _PdfUniqueEncodingElement.prototype._setTeleprinterText = function (value) {
        this._setValue(new Uint8Array(value));
    };
    _PdfUniqueEncodingElement.prototype._getVideoTextInformation = function () {
        return this._getOctetString();
    };
    _PdfUniqueEncodingElement.prototype._setVideoTextInformation = function (value) {
        this._setValue(new Uint8Array(value));
    };
    _PdfUniqueEncodingElement.prototype._getInternationalAlphabetString = function () {
        if (this._construction !== _ConstructionType.primitive) {
            throw new Error('ASCII string cannot be constructed.');
        }
        return _convertBytesToText(this._getValue());
    };
    _PdfUniqueEncodingElement.prototype._setInternationalAlphabetString = function (value) {
        this._setValue(_stringToBytes(value));
    };
    _PdfUniqueEncodingElement.prototype._getGraphicString = function () {
        if (this._construction !== _ConstructionType.primitive) {
            throw new Error('Graphic string cannot be constructed.');
        }
        return this._decodeGraphicString(this._getValue());
    };
    _PdfUniqueEncodingElement.prototype._setGraphicString = function (value) {
        this._setValue(this._encodeGraphicString(value));
    };
    _PdfUniqueEncodingElement.prototype._getVisibleString = function () {
        return this._decodeVisibleString(this._getValue());
    };
    _PdfUniqueEncodingElement.prototype._setVisibleString = function (value) {
        this._setValue(this._encodeVisibleString(value));
    };
    _PdfUniqueEncodingElement.prototype._getUniversalString = function () {
        var values = this._getValue();
        if (this._construction !== _ConstructionType.primitive) {
            throw new Error('Universal string cannot be constructed.');
        }
        if (values.length % 4) {
            throw new Error('Universal string encoded on non-mulitple of four bytes.');
        }
        var ret = '';
        for (var i = 0; i < values.length; i += 4) {
            ret += String.fromCharCode((values[i + 0] << 24)
                + (values[i + 1] << 16)
                + (values[i + 2] << 8)
                + (values[i + 3] << 0));
        }
        return ret;
    };
    _PdfUniqueEncodingElement.prototype._setUniversalString = function (value) {
        var buf = new Uint8Array(value.length << 2);
        for (var i = 0; i < value.length; i++) {
            buf[(i << 2)] = value.charCodeAt(i) >>> 24;
            buf[(i << 2) + 1] = value.charCodeAt(i) >>> 16;
            buf[(i << 2) + 2] = value.charCodeAt(i) >>> 8;
            buf[(i << 2) + 3] = value.charCodeAt(i);
        }
        this._setValue(buf);
    };
    _PdfUniqueEncodingElement.prototype._getBmpString = function () {
        var value = this._getValue();
        if (this._construction !== _ConstructionType.primitive) {
            throw new Error('BMPString cannot be constructed.');
        }
        if (value.length % 2 !== 0) {
            throw new Error('BMPString encoded on non-multiple of two bytes.');
        }
        if (typeof TextDecoder !== 'undefined') {
            return new TextDecoder('utf-16be').decode(value);
        }
        var result = '';
        for (var i = 0; i < value.length; i += 2) {
            var code = (value[i] << 8) | value[i + 1];
            result += String.fromCharCode(code);
        }
        return result;
    };
    _PdfUniqueEncodingElement.prototype._setBmpString = function (value) {
        var buf = new Uint8Array(value.length << 1);
        for (var i = 0; i < value.length; i++) {
            var code = value.charCodeAt(i);
            buf[i << 1] = code >> 8;
            buf[(i << 1) + 1] = code & 0xff;
        }
        this._setValue(buf);
    };
    _PdfUniqueEncodingElement.prototype._construct = function (els) {
        this._currentValueLength = undefined;
        this._value = els;
    };
    _PdfUniqueEncodingElement.prototype._encode = function (value) {
        switch (typeof value) {
            case ('undefined'): {
                this._setValue(new Uint8Array(0));
                break;
            }
            case ('boolean'): {
                this._setTagNumber(_UniversalType.abstractSyntaxBoolean);
                this._setBooleanValue(value);
                break;
            }
            case ('number'): {
                if (Number.isInteger(value)) {
                    this._setTagNumber(_UniversalType.integer);
                    this._setInteger(value);
                }
                break;
            }
            case ('string'): {
                this._setTagNumber(_UniversalType.utf8String);
                this._setUtf8String(value);
                break;
            }
            case ('object'): {
                if (!value) {
                    this._setTagNumber(_UniversalType.nullValue);
                    this._setValue(new Uint8Array(0));
                }
                else if (value instanceof Uint8Array) {
                    this._setTagNumber(_UniversalType.octetString);
                    this._setOctetString(value);
                }
                else if (value instanceof Uint8ClampedArray) {
                    this._setTagNumber(_UniversalType.bitString);
                    this._setBitString(value);
                }
                else if (value instanceof _PdfAbstractSyntaxElement) {
                    this._construction = _ConstructionType.constructed;
                    this._setSequence([value]);
                }
                else if (Array.isArray(value)) {
                    this._construction = _ConstructionType.constructed;
                    this._setTagNumber(_UniversalType.sequence);
                    this._setSequence(value.map(function (sub) {
                        var ret = new _PdfUniqueEncodingElement();
                        ret._encode(sub);
                        return ret;
                    }));
                }
                else if (value instanceof _PdfObjectIdentifier) {
                    this._setTagNumber(_UniversalType.objectIdentifier);
                    this._setObjectIdentifier(value);
                }
                else {
                    throw new Error("Cannot encode value of type " + value.constructor.name + ".");
                }
                break;
            }
            default: {
                throw new Error("Cannot encode value of type " + typeof value + ".");
            }
        }
    };
    _PdfUniqueEncodingElement.prototype._fromSet = function (set) {
        var ret = new _PdfUniqueEncodingElement(_TagClassType.universal, _ConstructionType.constructed, _UniversalType.abstractSyntaxSet);
        ret._setAbstractSetValue(set.filter(function (element) { return Boolean(element); }));
        return ret;
    };
    _PdfUniqueEncodingElement.prototype._fromSetOf = function (set) {
        var ret = new _PdfUniqueEncodingElement(_TagClassType.universal, _ConstructionType.constructed, _UniversalType.abstractSyntaxSet);
        ret._setAbstractSetOf(set.filter(function (element) { return Boolean(element); }));
        return ret;
    };
    _PdfUniqueEncodingElement.prototype._getInner = function () {
        if (this._construction !== _ConstructionType.constructed) {
            throw new Error('An explicitly-encoded element cannot be encoded using primitive construction.');
        }
        if (Array.isArray(this._value)) {
            if (this._value.length !== 1) {
                throw new Error("An explicitly-encoding element contained " + this._value.length + " encoded elements.");
            }
            return this._value[0];
        }
        var ret = new _PdfUniqueEncodingElement();
        var readBytes = ret._fromBytes(this._value);
        if (readBytes !== this._value.length) {
            throw new Error('An explicitly-encoding element contained more than one single ');
        }
        return ret;
    };
    _PdfUniqueEncodingElement.prototype._setInner = function (value) {
        this._construction = _ConstructionType.constructed;
        this._value = [value];
    };
    _PdfUniqueEncodingElement.prototype._fromBytes = function (bytes) {
        if (bytes.length < 2) {
            throw new Error('Tried to decode a DER element that is less than two bytes.');
        }
        var cursor = 0;
        switch (bytes[cursor] & 192) {
            case (0):
                this._tagClass = _TagClassType.universal;
                break;
            case (64):
                this._tagClass = _TagClassType.application;
                break;
            case (128):
                this._tagClass = _TagClassType.context;
                break;
            case (192):
                this._tagClass = _TagClassType.abstractSyntaxPrivate;
                break;
        }
        this._construction = ((bytes[cursor] & 32)
            ? _ConstructionType.constructed : _ConstructionType.primitive);
        this._setTagNumber((bytes[cursor] & 31));
        cursor++;
        var tagNumber = this._getTagNumber();
        if (tagNumber >= 31) {
            if (bytes[cursor] === 128) {
                throw new Error('Leading padding byte on long tag number encoding.');
            }
            tagNumber = 0;
            var limit = (((bytes.length - 1) >= 4) ? 4 : (bytes.length - 1));
            while (cursor < limit) {
                if (!(bytes[cursor++] & 128)) {
                    break;
                }
            }
            if (bytes[cursor - 1] & 128) {
                if (limit === (bytes.length - 1)) {
                    throw new Error('ASN1 tag number appears to have been truncated.');
                }
                else {
                    throw new Error('ASN1 tag number too large.');
                }
            }
            for (var i = 1; i < cursor; i++) {
                tagNumber <<= 7;
                tagNumber |= (bytes[i] & 0x7F);
            }
            if (tagNumber < 31) {
                throw new Error('ASN1 tag number could have been encoded in short form.');
            }
        }
        if ((bytes[cursor] & 128) === 128) {
            var numberOfLengthOctets = (bytes[cursor] & 0x7F);
            if (numberOfLengthOctets === 127) { // Reserved
                throw new Error('Length byte with undefined meaning encountered.');
            }
            if (numberOfLengthOctets > 4) {
                throw new Error('Element length too long to decode to an integer.');
            }
            if (cursor + numberOfLengthOctets >= bytes.length) {
                throw new Error('Element length bytes appear to have been truncated.');
            }
            cursor++;
            var lengthNumberOctets = new Uint8Array(4);
            for (var i = numberOfLengthOctets; i > 0; i--) {
                lengthNumberOctets[(4 - i)] = bytes[(cursor + numberOfLengthOctets - i)];
            }
            var length_1 = 0;
            lengthNumberOctets.forEach(function (octet) {
                length_1 <<= 8;
                length_1 += octet;
            });
            if ((cursor + length_1) < cursor) {
                throw new Error('ASN1 element too large.');
            }
            cursor += (numberOfLengthOctets);
            if ((cursor + length_1) > bytes.length) {
                throw new Error('ASN1 element truncated.');
            }
            if (((length_1 <= 127 && length_1 >= -128) && numberOfLengthOctets > 1)
                || ((length_1 <= 32767 && length_1 >= -32768) && numberOfLengthOctets > 2)
                || ((length_1 <= 8388607 && length_1 >= -8388608) && numberOfLengthOctets > 3)) {
                throw new Error('DER-encoded long-form length encoded on more octets than necessary');
            }
            this._setValue(bytes.slice(cursor, (cursor + length_1)));
            return (cursor + length_1);
        }
        else {
            var length_2 = (bytes[cursor++] & 0x7F);
            if ((cursor + length_2) > bytes.length) {
                throw new Error('ASN1 element was truncated.');
            }
            this._setValue(bytes.slice(cursor, (cursor + length_2)));
            return (cursor + length_2);
        }
    };
    _PdfUniqueEncodingElement.prototype._tagAndLengthBytes = function () {
        var tagNumber = this._getTagNumber();
        var first = ((this._tagClass & 0x03) << 6) | ((this._construction & 0x01) << 5);
        var tagBytes = [];
        if (tagNumber < 31) {
            tagBytes = [first | (tagNumber & 0x1f)];
        }
        else {
            tagBytes = [first | 0x1f];
            var tmp = [];
            var n = tagNumber;
            do {
                tmp.unshift(n & 0x7f);
                n >>>= 7;
            } while (n > 0);
            for (var i = 0; i < tmp.length; i++) {
                tagBytes.push(i < tmp.length - 1 ? (tmp[i] | 0x80) : tmp[i]);
            }
        }
        var lengthOctets = this._getLengthOctets();
        var ret = new Uint8Array(tagBytes.length + lengthOctets.length);
        ret.set(tagBytes, 0);
        ret.set(lengthOctets, tagBytes.length);
        return ret;
    };
    _PdfUniqueEncodingElement.prototype._toBuffers = function () {
        var head = this._tagAndLengthBytes();
        var out = [head];
        if (Array.isArray(this._value)) {
            for (var _i = 0, _a = this._value; _i < _a.length; _i++) {
                var el = _a[_i];
                out.push.apply(out, el._toBuffers());
            }
        }
        else {
            out.push(this._serialize());
        }
        return out;
    };
    _PdfUniqueEncodingElement.prototype._serialize = function () {
        return new Uint8Array(this._getValue());
    };
    _PdfUniqueEncodingElement.prototype._getLengthOctets = function () {
        var length = this._valueLength();
        if (length < 0x80) {
            return [length];
        }
        var octets = [];
        var v = length;
        while (v > 0) {
            octets.unshift(v & 0xff);
            v >>>= 8;
        }
        return [0x80 | octets.length].concat(octets);
    };
    _PdfUniqueEncodingElement.prototype._getComponents = function () {
        if (Array.isArray(this._value)) {
            return this._value;
        }
        var encodedElements = [];
        var i = 0;
        while (i < this._value.length) {
            var next = new _PdfUniqueEncodingElement();
            i += next._fromBytes(this._getValue().subarray(i));
            encodedElements.push(next);
        }
        return encodedElements;
    };
    _PdfUniqueEncodingElement.prototype._decodeBitString = function (value) {
        if (value.length === 0) {
            throw new Error('ASN1 bit string cannot be encoded on zero bytes!');
        }
        if (value.length === 1 && value[0] !== 0) {
            throw new Error('ASN1 bit string encoded with deceptive first byte!');
        }
        if (value[0] > 7) {
            throw new Error('First byte of an ASN1 bit string must be <= 7!');
        }
        var ret = [];
        for (var i = 1; i < value.length; i++) {
            var byte = value[i];
            ret.push((byte & 128) ? 1 : 0, (byte & 64) ? 1 : 0, (byte & 32) ? 1 : 0, (byte & 16) ? 1 : 0, (byte & 8) ? 1 : 0, (byte & 4) ? 1 : 0, (byte & 2) ? 1 : 0, (byte & 1) ? 1 : 0);
        }
        var unusedBits = value[0];
        var trailingBits = ret.slice(ret.length - unusedBits);
        for (var _i = 0, trailingBits_1 = trailingBits; _i < trailingBits_1.length; _i++) {
            var bit = trailingBits_1[_i];
            if (bit !== 0) {
                throw new Error('bit string had a trailing set bit.');
            }
        }
        ret.length -= unusedBits;
        return new Uint8ClampedArray(ret);
    };
    _PdfUniqueEncodingElement.prototype._decodeBoolean = function (value) {
        if (value.length !== 1) {
            throw new Error('Invalid Boolean format: Boolean values must be exactly one byte.');
        }
        if (value[0] !== 0x00 && value[0] !== 0xff) {
            throw new Error('Boolean must be encoded as 0xFF or 0x00.');
        }
        return value[0] !== 0x00;
    };
    _PdfUniqueEncodingElement.prototype._encodeCharacterString = function (value) {
        return this._encodeSequence([
            value._identification,
            new _PdfUniqueEncodingElement(_TagClassType.universal, _ConstructionType.primitive, _UniversalType.octetString, value._stringValue)
        ]);
    };
    _PdfUniqueEncodingElement.prototype._decodeSequence = function (value) {
        if (value.length === 0) {
            return [];
        }
        var encodedElements = [];
        var i = 0;
        while (i < value.length) {
            var next = new _PdfUniqueEncodingElement();
            var consumed = next._fromBytes(value.subarray(i));
            i += consumed;
            encodedElements.push(next);
        }
        return encodedElements;
    };
    _PdfUniqueEncodingElement.prototype._fromSequence = function (sequence) {
        var ret = new _PdfUniqueEncodingElement(_TagClassType.universal, _ConstructionType.constructed, _UniversalType.sequence);
        ret._setSequence(sequence.filter(function (element) { return Boolean(element); }));
        return ret;
    };
    _PdfUniqueEncodingElement.prototype._getExternalEncoding = function (encodingElement) {
        switch (encodingElement._getTagNumber()) {
            case 0:
                return encodingElement._getInner();
            case 1:
                return encodingElement._getOctetString();
            case 2:
                return encodingElement._getBitString();
            default:
                throw new Error('External does not know of an encoding option ' +
                    ("having tag number " + encodingElement._getTagNumber() + "."));
        }
    };
    _PdfUniqueEncodingElement.prototype._valueLength = function () {
        if (typeof this._currentValueLength !== 'undefined' && this._currentValueLength !== null && !Array.isArray(this._value)) {
            return this._currentValueLength;
        }
        if (!Array.isArray(this._value)) {
            var v = this._getValue();
            this._currentValueLength = v.length;
            return v.length;
        }
        var len = 0;
        for (var _i = 0, _a = this._value; _i < _a.length; _i++) {
            var el = _a[_i];
            len += el._tagValueLength(); //eslint-disable-line
        }
        this._currentValueLength = len;
        return len;
    };
    _PdfUniqueEncodingElement.prototype._lengthLength = function (valueLength) {
        var len = (valueLength !== null && valueLength !== undefined)
            ? valueLength
            : this._valueLength();
        if (len < 128) {
            return 1;
        }
        var n = 0;
        var v = len;
        while (v > 0) {
            n++;
            v >>>= 8;
        }
        return 1 + n;
    };
    _PdfUniqueEncodingElement.prototype._tagValueLength = function () {
        var value = this._valueLength();
        return this._tagLength() + this._lengthLength(value) + value;
    };
    return _PdfUniqueEncodingElement;
}(_PdfAbstractSyntaxElement));
export { _PdfUniqueEncodingElement };
